import { useEffect, useRef } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Panel } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { trpc } from '../lib/trpc';

/**
 * Where MBIE sends an administrator back to after they sign in to Tenancy Services, with `code`
 * and `state` on the URL — the Trade Me callback's job, for OAuth 2.0.
 *
 * Hand-parsed for that page's reason: `validateSearch` runs in the eager route tree. The server
 * bounds both values and matches `state` against an approval this person started.
 */
interface TenancyServicesCallbackSearch {
  code?: string;
  state?: string;
  error?: string;
}

const text = (value: unknown, max: number) =>
  typeof value === 'string' && value.length > 0 && value.length <= max ? value : undefined;

export const Route = createFileRoute('/settings/integrations/tenancy-services/callback')({
  validateSearch: (search: Record<string, unknown>): TenancyServicesCallbackSearch => {
    const code = text(search.code, 4000);
    const state = text(search.state, 64);
    const error = text(search.error, 100);
    return {
      ...(code ? { code } : {}),
      ...(state ? { state } : {}),
      ...(error ? { error } : {}),
    };
  },
  component: TenancyServicesCallbackPage,
});

function TenancyServicesCallbackPage() {
  const { code, state } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const complete = useMutation(
    trpc.integrations.tenancyServices.complete.mutationOptions({
      meta: { errorTitle: 'Tenancy Services could not be connected' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.list.queryKey() });
        // REPLACE, so Back does not land here and try to spend the same approval twice.
        void navigate({
          to: '/settings/integrations',
          search: { connected: 'tenancyservices' },
          replace: true,
        });
      },
    }),
  );

  // Once, however often React mounts this — see the Trade Me callback.
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !code || !state) return;
    sent.current = true;
    complete.mutate({ code, state });
  }, [complete, code, state]);

  // Sent back without a code when the sign-in was declined or abandoned.
  const declined = !code || !state;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Connecting Tenancy Services"
        subtitle="Finishing the sign-in you gave on Tenancy Services"
      />

      <Panel className="px-5 py-5">
        {declined ? (
          <p role="alert" className="text-[0.875rem] font-medium">
            Tenancy Services did not send an approval back, so nothing was connected. If you meant
            to connect, start again.
          </p>
        ) : complete.isError ? (
          <p role="status" className="text-[0.875rem] font-medium">
            Nothing was connected. Start again from Integrations once the reason is dealt with.
          </p>
        ) : (
          <p role="status" className="text-[0.875rem] text-ink-muted">
            Connecting to Tenancy Services…
          </p>
        )}

        {declined || complete.isError ? (
          <Link
            to="/settings/integrations"
            className="mt-4 inline-block text-[0.875rem] font-semibold text-accent hover:underline"
          >
            Back to Integrations
          </Link>
        ) : null}
      </Panel>
    </div>
  );
}
